/**
 * PublicCalendarEventPage - Public view of a single calendar event
 *
 * Route: /public/calendar/events/:eventId
 *
 * Shows details for a specific calendar event without requiring authentication.
 * Uses the shared CalendarDayView component for consistent rendering.
 */

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Calendar as CalendarIcon,
  AlertCircle,
  Loader2,
  Download
} from 'lucide-react';
import PublicLayout from '@shared/ui/PublicLayout';
import Button from '../components/Button';
import CalendarDayView from '../components/calendar/CalendarDayView';
import { CalendarEventDisplay } from '../utils/calendarUtils';
import { downloadICSFile } from '../utils/calendarExport';

// API client without auth
const publicApi = axios.create({
  baseURL: '/api',
});

interface EventMetadata {
  title: string;
  subtitle: string;
  date: string;
  description: string;
}

interface PublicEventData {
  event: CalendarEventDisplay;
  metadata: EventMetadata;
}

// Fetch event data
const fetchPublicEvent = async (eventId: string): Promise<PublicEventData> => {
  const response = await publicApi.get(`/public/calendar/events/${eventId}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to load event');
  }
  return response.data.data;
};

const PublicCalendarEventPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();

  // Fetch event data
  const { data: eventData, isLoading, error } = useQuery(
    ['public-event', eventId],
    () => fetchPublicEvent(eventId!),
    {
      enabled: !!eventId,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  // Handle ICS download for the event (uses first bill if available)
  const handleDownloadEventICS = () => {
    if (!eventData?.event) return;

    const event = eventData.event;
    const firstBill = event.bills?.[0];

    try {
      downloadICSFile({
        eventId: event.calendar_id,
        title: event.reading_title || 'Legislative Event',
        date: event.reading_date,
        time: event.reading_time,
        location: event.room_number,
        chamber: event.chamber,
        noTime: event.no_time,
        uponAdjournment: event.upon_adjournment,
        billNumbers: event.bills?.map(b => b.number),
        billNumber: firstBill?.number,
        billTitle: firstBill?.title || firstBill?.concerning,
      });
      toast.success('Calendar event downloaded');
    } catch (err) {
      toast.error('Failed to download calendar event');
    }
  };

  // Format display date
  const formatDisplayDate = (dateStr: string) => {
    try {
      const dateOnly = (dateStr || '').split('T')[0];
      const date = new Date(dateOnly + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get date from event for "View All Events" link
  const eventDate = eventData?.event?.reading_date
    ? (typeof eventData.event.reading_date === 'string'
        ? eventData.event.reading_date.split('T')[0]
        : eventData.event.reading_date)
    : null;

  // The shared public chrome — header, responsive gutters, footer with the
  // sign-in prompt — instead of a hand-rolled copy (Angie, 2026-09-13: one
  // frame for every public page).
  return (
    <PublicLayout
      loginReturnUrl={eventDate ? `/calendar/${eventDate}` : '/calendar'}
      backHref={eventDate ? `/public/calendar/all/${eventDate}` : undefined}
      backLabel={eventDate ? `‹ View all events for ${formatDisplayDate(String(eventDate))}` : undefined}
    >
          <div className="flex items-center gap-4 mb-6">
            <CalendarIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">
                Colorado Legislative Calendar
              </h1>
              <p className="text-text-secondary dark:text-text-dark-secondary">
                Event Details
              </p>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
              <span className="ml-3 text-text-secondary dark:text-text-dark-secondary">
                Loading event...
              </span>
            </div>
          )}

          {/* Error State */}
          {!!error && (
            <div className="card text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                Event Not Found
              </h2>
              <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
                {error instanceof Error ? error.message : 'Unable to load event details.'}
              </p>
              <Link to="/public/calendar/all/today">
                <Button variant="primary">
                  View Today&apos;s Calendar
                </Button>
              </Link>
            </div>
          )}

          {/* Event Content */}
          {eventData && (
            <>
              {/* Event Date Header */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
                  {formatDisplayDate(eventData.metadata.date)}
                </h2>
                <p className="text-text-secondary dark:text-text-dark-secondary">
                  {eventData.metadata.subtitle}
                </p>
              </div>

              {/* Event Card - Using shared component */}
              <CalendarDayView
                events={[eventData.event]}
                viewMode="card"
                isPublic={true}
              />

              {/* Footer Actions */}
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={handleDownloadEventICS}
                  className="inline-flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Add to Calendar (.ics)
                </Button>

                {eventDate && (
                  <Link to={`/public/calendar/all/${eventDate}`}>
                    <Button variant="secondary">
                      View All Events This Day
                    </Button>
                  </Link>
                )}
              </div>
            </>
          )}

          <p className="mt-8 text-center text-sm text-text-tertiary dark:text-text-dark-tertiary">
            Colorado Legislative Calendar ·{' '}
            <a
              href="https://leg.colorado.gov/content/calendars"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Official Colorado General Assembly
            </a>
          </p>
    </PublicLayout>
  );
};

export default PublicCalendarEventPage;
